import { Target } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

const frequencyOptions = [
  "Nikad",
  "Veoma retko",
  "Retko",
  "Ponekad",
  "Često",
  "Skoro uvek",
];

const increasingScores = [0, 1, 2, 3, 4, 5];

export const testPerfekcionizma: QuizConfig = {
  id: "test-perfekcionizma",
  illustration: {
    src: "/images/quizzes/test-perfekcionizma.webp",
    alt: "Žena prepravlja gotovo završen crtež okružena ranijim skicama.",
  },
  lastModified: "2026-10-05",
  title: "Test perfekcionizma: Da li te standardi iscrpljuju?",
  shortTitle: "Test perfekcionizma",
  description:
    "Test perfekcionizma otkriva da li strah od greške, strogi standardi i samokritika upravljaju tvojim danom. Ovo je samoprocena, ne dijagnoza.",
  longDescription:
    "Ovaj test perfekcionizma kroz 11 pitanja sagledava strah od greške, preterano proveravanje, odlaganje i samokritiku tokom poslednjih mesec dana. Rezultat pokazuje koliko ti strogi standardi otežavaju svakodnevicu. Test služi za samoprocenu i ne postavlja dijagnozu.",
  icon: Target,
  duration: "3-4 min",
  category: "Lični razvoj",
  featured: false,
  focusAreasWanted: [
    "perfectionism",
    "self_esteem",
    "procrastination",
    "shame_guilt",
  ],
  relatedDirectorySlugs: ["perfekcionizam", "samopouzdanje"],
  questions: [
    {
      type: "radio",
      question:
        "Kada napraviš grešku, koliko često ona promeni način na koji vidiš svoju vrednost?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često odlažeš početak zadatka jer sumnjaš da će rezultat biti dovoljno dobar?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često proveravaš ili prepravljaš već završen rad iako ispunjava ono što se traži?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često ti je teško da završiš zadatak jer uvek vidiš još nešto što treba popraviti?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često svoj rezultat porediš sa najboljim mogućim umesto sa onim što je dovoljno dobro?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question: "Koliko često kritiku ili ispravku doživiš kao lični neuspeh?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često izbegneš novi izazov zbog mogućnosti da ne uspeš?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često osećaš krivicu dok odmaraš jer obaveze nisu savršeno završene?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često tvoji standardi stvaraju napetost, iscrpljenost ili sukobe sa drugima?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često možeš da prihvatiš dovoljno dobar rezultat i nastaviš dalje bez vraćanja na njega?",
      options: frequencyOptions,
      scores: [5, 4, 3, 2, 1, 0],
    },
    {
      type: "textarea",
      question:
        "Opiši situaciju u kojoj su ti tvoji visoki standardi otežali svakodnevicu:",
    },
  ],
  copy: {
    title: "Tvoj odnos prema greškama i standardima",
    subtitle: "Razumevanje perfekcionističkih obrazaca",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Kako da ublažiš pritisak",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
  promptExtension: `
Ovo je samoprocena rigidnih i opterećujućih perfekcionističkih obrazaca, nije dijagnostički test. Pitanja su originalna i oslanjaju se na istraživačke domene brige zbog grešaka, sumnje u sopstvene postupke i visokih standarda. Kao stručni okvir koristi dvodimenzionalni model F-MPS-Brief, bez tvrdnje da je ovaj kviz njegova validirana verzija: https://doi.org/10.1177/0734282916651359

Tumači ukupni rezultat ovako:
- 0-39: rigidni perfekcionistički obrasci su retki ili blagi
- 40-60: obrasci su umereno prisutni i povremeno stvaraju pritisak
- 61-100: obrasci su izraženi i mogu uticati na funkcionisanje ili dobrobit

Ovi rasponi su interne, nekliničke smernice proizvoda. Nisu validirani pragovi i ne smeju se predstavljati kao kliničke granice.

Obavezna pravila:
- Rezultat opisuj kao pokazatelj ili tendenciju, nikada kao dijagnozu ili dokaz da je osoba perfekcionista.
- Ne predstavljaj visoke standarde kao problem sami po sebi. Fokusiraj se na rigidnost, strah od greške, cenu po svakodnevni život i sposobnost da se prihvati dovoljno dobro.
- Ne zaključuj da osoba ima nisko samopouzdanje, prokrastinaciju, burnout, ADHD, OKP ili poremećaj ličnosti. Te teme pominji samo ako konkretni odgovori ukazuju na njih, i tada kao moguću vezu, ne kao uzrok ili dijagnozu.
- Za rezultat iznad 60 ili odgovor koji pokazuje izražen uticaj na svakodnevni život preporuči razgovor sa psihologom ili psihoterapeutom, bez zastrašivanja.
- Predloži konkretne korake: unapred definisan kriterijum "dovoljno dobro", vremensko ograničenje za proveravanje, namerno završavanje malog zadatka bez dodatnog doterivanja i odvajanje lične vrednosti od jednog rezultata.
- isInNeedForTherapy postavi na true kada je rezultat iznad 60 ili odgovori pokazuju izraženo narušavanje funkcionisanja. U ostalim slučajevima postavi ga na false.
`,
  landing: {
    summary:
      "Visoki standardi mogu da podstaknu trud. Problem nastaje kada greška postane merilo lične vrednosti, a svaki zadatak mora da bude besprekorno završen.",
    highlights: [
      {
        title: "Odnos prema greškama",
        description:
          "Prepoznaj da li grešku vidiš kao informaciju ili kao lični neuspeh.",
      },
      {
        title: "Cena visokih standarda",
        description:
          "Sagledaj da li proveravanje, odlaganje i teško prihvatanje odmora stvaraju pritisak.",
      },
      {
        title: "Fleksibilnost",
        description:
          "Proveri koliko lako prihvataš dovoljno dobar rezultat i nastavljaš dalje.",
      },
    ],
    whoIsItFor:
      "Za tebe ako često prepravljaš završen posao, teško podnosiš greške ili osećaš da nikada nije dovoljno dobro.",
    whatYouLearn:
      "Dobićeš pokazatelj koliko rigidni standardi utiču na tvoje odluke, odmor, rad i odnos prema sebi.",
    howItWorks:
      "Odgovori na 11 pitanja prema tome kako si se osećao/la i ponašao/la tokom poslednjih mesec dana. Nema tačnih i pogrešnih odgovora.",
    nextSteps:
      "Rezultat nije dijagnoza. Ako ti strah od greške ili strogi standardi značajno otežavaju svakodnevni život, razgovor sa psihologom ili psihoterapeutom može pomoći da razumeš obrazac i pronađeš fleksibilniji pristup.",
    faq: [
      {
        question: "Da li ovaj test postavlja dijagnozu?",
        answer:
          "Ne. Ovo je alat za samoprocenu perfekcionističkih obrazaca. Dijagnozu može postaviti samo kvalifikovani stručnjak nakon odgovarajuće procene.",
      },
      {
        question: "Da li su visoki standardi uvek problem?",
        answer:
          "Ne. Važno je koliko su fleksibilni i kakvu cenu imaju. Standardi postaju opterećujući kada greške izazivaju jak strah, zadaci se teško završavaju ili trpe odmor, odnosi i svakodnevno funkcionisanje.",
      },
      {
        question: "Na koji period da mislim dok odgovaram?",
        answer:
          "Razmišljaj o poslednjih mesec dana. Tako će odgovori bolje opisati tvoj trenutni obrazac, a ne samo jedan posebno dobar ili težak dan.",
      },
    ],
    ctaLabel: "Pokreni test perfekcionizma",
  },
};
